import {
  FeaturesState,
  selectFeaturesSlice,
} from "@/store/features/features-slice";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { selectSubscriptionFeaturesSlice } from "@/store/subscription-features/subscription-features-selectors";
import { SubscriptionFeaturesState } from "@/store/subscription-features/subscription-features-slice";
import {
  isIElementOrthomosaic,
  isIElementPhotogrammetryScan,
  isLocationMarker,
} from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import { isIElementViewableGaussianSplats } from "@/types/type-guards";
import { walkWithQueue } from "@faro-lotv/foundation";
import {
  IElement,
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementCAD,
  isIElementGaussianSplats,
  isIElementGenericDataSession,
  isIElementGenericDataset,
  isIElementGenericPointCloudStream,
  isIElementImg2d,
  isIElementImg360,
  isIElementOdometryPathSection,
  isIElementSectionWithTypeHint,
  isIElementTimeSeriesRoom,
  validateKnownIElementTypes,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  isSingleScanSection,
  newestToOldest,
  selectAncestor,
  selectChildDepthFirst,
  selectDataSessionContainedData,
  selectIElementChildren,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { partition } from "es-toolkit";
import { aiAssetReviewMode } from "./ai-asset-review-mode/ai-asset-review-mode";
import { alignWizardMode } from "./alignment-wizard/align-wizard-mode";
import { cloudCadAlignmentMode } from "./cloud-cad-alignment-mode/cloud-cad-alignment-mode";
import { cloudToSheetAlignmentMode } from "./cloud-to-sheet-alignment-mode/cloud-to-sheet-alignment-mode";
import { controlPointsAlignmentMode } from "./control-points-alignment-mode/control-points-alignment-mode";
import { createAreaMode } from "./create-area-mode/create-area-mode";
import { exportMode } from "./export-mode/export-mode";
import { floorScaleMode } from "./floor-scale-mode/floor-scale-mode";
import { flyWalkMode } from "./flywalk-mode/flywalk-mode";
import { importSheetMode } from "./import-sheet-mode/import-sheet-mode";
import { ipxOrderMode } from "./ipx-order-mode/ipx-order-mode";
import { Mode, ModeNames } from "./mode";
import { selectDataSet360s } from "./mode-selectors";
import { overviewMode } from "./overview-mode/overview-mode";
import { pathAlignmentMode } from "./path-alignment-mode/path-alignment-mode";
import { projectConversionMode } from "./project-conversion-mode/project-conversion-mode";
import { selectIsPointCloudViewable } from "./select-current-area-data";
import { sheetCadAlignmentMode } from "./sheet-cad-alignment-mode/sheet-cad-alignment-mode";
import { sheetMode } from "./sheet-mode/sheet-mode";
import { sheetToCloudAlignmentMode } from "./sheet-to-cloud-alignment-mode/sheet-to-cloud-alignment-mode";
import { sheetToSheetAlignmentMode } from "./sheet-to-sheet-alignment-mode/sheet-to-sheet-alignment-mode";
import { splitMode } from "./split-mode/split-mode";
import { startMode } from "./start-mode";
import { tagsManagementMode } from "./tags-management-mode/tags-management-mode";

/**
 * All the available modes loaded for code splitting;
 */
export const MODES: Record<ModeNames, Mode> = {
  start: startMode,
  sheet: sheetMode,
  overview: overviewMode,
  walk: flyWalkMode,
  split: splitMode,
  pathAlignment: pathAlignmentMode,
  floorscale: floorScaleMode,
  createArea: createAreaMode,
  sheetCadAlignment: sheetCadAlignmentMode,
  projectConversion: projectConversionMode,
  cloudCadAlignment: cloudCadAlignmentMode,
  alignWizard: alignWizardMode,
  sheetToCloudAlignment: sheetToCloudAlignmentMode,
  export: exportMode,
  importSheet: importSheetMode,
  sheetToSheetAlignment: sheetToSheetAlignmentMode,
  controlPointsAlignment: controlPointsAlignmentMode,
  cloudToSheetAlignment: cloudToSheetAlignmentMode,
  tagsManagement: tagsManagementMode,
  aiAssetReview: aiAssetReviewMode,
  ipxOrder: ipxOrderMode,
};

/**
 * Load a mode
 *
 * @param name the mode name
 * @returns A promise that will resolve when the mode is loaded
 */
export function getMode(name: ModeNames): Mode {
  return MODES[name];
}

/**
 * Narrows an arbitrary string to a mode name.
 *
 * A mode name reaching the app from outside - a deep link query param, a saved
 * view's `mode` field - is just a string, and `getMode` returns `undefined` for
 * anything unknown. Guarding before `getMode` keeps that `undefined` from
 * reaching mode machinery that assumes a real mode.
 *
 * Own keys only: `in` would walk the prototype chain, so `mode=toString` would
 * pass and `getMode` would hand back a function instead of a mode.
 *
 * @param name The value to test.
 * @returns whether the value names one of the registered modes.
 */
export function isModeName(name: string | undefined): name is ModeNames {
  return !!name && Object.prototype.hasOwnProperty.call(MODES, name);
}

type IElementsFeaturesStore = {
  iElements: IElementsState;
  features: FeaturesState;
};

/**
 * Map each type of IElement to the default Mode used to render it
 */
const DEFAULT_MODES_MAP: Partial<
  Record<
    IElementType,
    (
      el: IElement,
      { iElements, features }: IElementsFeaturesStore,
    ) => ModeNames | undefined
  >
> = {
  [IElementType.img360]: () => "walk",
  [IElementType.timeSeries]: (el) => {
    switch (el.typeHint) {
      case IElementTypeHint.dataSession:
      case IElementTypeHint.bimModel:
        return "overview";
      case IElementTypeHint.videoRecordings:
        return "sheet";
    }
  },
  [IElementType.section]: (el, { iElements }: OnlyIElementsStore) => {
    if (el.typeHint === IElementTypeHint.dataSession) {
      const { panos, pointClouds, gaussianSplats } =
        selectDataSessionContainedData(el, {
          pointCloudFilter: (pc) =>
            selectIsPointCloudViewable(pc)({ iElements }),
          gaussianSplatsFilter: isIElementViewableGaussianSplats,
        })({ iElements });
      if (panos) {
        return "sheet";
      }
      if (pointClouds || gaussianSplats) {
        return "overview";
      }
    } else if (isLocationMarker(el, { iElements })) {
      return "sheet";
    } else if (
      isIElementSectionWithTypeHint(el, IElementTypeHint.orbisScan) ||
      isIElementSectionWithTypeHint(el, IElementTypeHint.roviScan)
    ) {
      // Orbis mobile scans should bring the user in sheet mode
      return "sheet";
    } else if (isSingleScanSection(el)) {
      if (
        el.typeHint === IElementTypeHint.siteScapeScan ||
        el.typeHint === IElementTypeHint.unstructuredScan
      ) {
        return "overview";
      }
      return "walk";
    } else if (isIElementAreaSection(el)) {
      return "sheet";
    }
  },
  [IElementType.imgSheet]: () => "sheet",
  [IElementType.imgSheetTiled]: () => "sheet",
  [IElementType.model3dStream]: () => "overview",
  [IElementType.pointCloudStreamWebShare]: () => "overview",
  [IElementType.pointCloudStream]: () => "overview",
  [IElementType.img2d]: () => "walk",
};

type DefaultModeForReturn = {
  /** The mode to transition to */
  targetMode: ModeNames;

  /**
   * The scene filters possible for the element to be visible.
   * Can contain multiple entries, if the element is visible in multiple scene filters,
   * or can be empty, if the best scene filter isn't known.
   */
  sceneFilters: SceneFilter[];

  /** The effective element to make active */
  element: IElement;
};

type IElementsSubscriptionsFeaturesStore = {
  iElements: IElementsState;
  subscriptionFeatures: SubscriptionFeaturesState;
  features: FeaturesState;
};

/**
 * Compute the default mode to explore a specific IElement
 *
 * @param state the state of the app store
 * @param targetOrSection The element we want to compute the default mode for
 * @param shouldUseIntensityData Whether intensity data should be used
 * @returns The mode to use for the targetOrSection and what element to make active, undefined if no mode is available
 */
export const selectDefaultModeFor = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectSubscriptionFeaturesSlice,
      selectFeaturesSlice,
      (state: IElementsSubscriptionsFeaturesStore, targetOrSection: IElement) =>
        targetOrSection,
      (
        state: IElementsSubscriptionsFeaturesStore,
        targetOrSection: IElement,
        shouldUseIntensityData: boolean,
      ) => shouldUseIntensityData,
    ],
    computeDefaultModeFor,
  ),
);

/**
 * Computes the default mode for a given project element.
 *
 * @param iElements The iElements slice of the store
 * @param subscriptionFeatures The subscriptions slice of the store
 * @param features The features slice of the store
 * @param targetOrSection The iElement to query the default mode for
 * @param shouldUseIntensityData Whether intensity data should be used
 * @returns Information to start up the appropriate mode and scene filters for the given element.
 */
export function computeDefaultModeFor(
  iElements: IElementsState,
  subscriptionFeatures: SubscriptionFeaturesState,
  features: FeaturesState,
  targetOrSection: IElement,
  shouldUseIntensityData: boolean,
): DefaultModeForReturn | undefined {
  return walkWithQueue(
    [targetOrSection],
    (current, append): DefaultModeForReturn | undefined => {
      if (!validateKnownIElementTypes(current.type)) return;
      const modeName = DEFAULT_MODES_MAP[current.type]?.(current, {
        iElements,
        features,
      });
      const mode = modeName ? MODES[modeName] : undefined;

      const targetElement = selectTargetElement(
        { iElements },
        current,
        shouldUseIntensityData,
      );

      if (
        mode &&
        (!mode.canBeStartedWith ||
          mode.canBeStartedWith(targetElement, {
            iElements,
            subscriptionFeatures,
            features,
          }))
      ) {
        return {
          targetMode: mode.name,
          element: targetElement,
          sceneFilters: getSceneFiltersForElement(targetElement, iElements),
        };
      }
      const children = selectIElementChildren(current.id)({ iElements });

      // Photo 360 has the priority to be selected
      const timeSeriesWithPanos = children.filter((el) => {
        if (isIElementTimeSeriesRoom(el)) {
          return selectChildDepthFirst(el, isIElementImg360)({ iElements });
        }
        return false;
      });
      if (timeSeriesWithPanos.length) {
        append(...timeSeriesWithPanos);
      } else {
        // other elements have priority to be selected
        const [locationMarkers, others] = partition(children, (element) =>
          isLocationMarker(element, { iElements }),
        );
        others.sort(newestToOldest);
        locationMarkers.sort(newestToOldest);
        if (others.length) append(...others);
        else append(...locationMarkers);
      }
    },
  );
}

/**
 * @param target The element that should be visible
 * @param iElements The iElements slice of the store
 * @returns the scene filters the element is visible in, the first one being the default
 */
export function getSceneFiltersForElement(
  target: IElement,
  iElements: IElementsState,
): SceneFilter[] {
  if (isIElementImg360(target) || isIElementImg2d(target)) {
    return [SceneFilter.Pano];
  } else if (
    isIElementGenericDataSession(target) ||
    isIElementGenericDataset(target)
  ) {
    const { panos, pointClouds, gaussianSplats } =
      selectDataSessionContainedData(target, {
        pointCloudFilter: (pc) => selectIsPointCloudViewable(pc)({ iElements }),
        gaussianSplatsFilter: isIElementViewableGaussianSplats,
      })({ iElements });
    return [
      ...(panos ? [SceneFilter.Pano] : []),
      ...(pointClouds ? [SceneFilter.PointCloud, SceneFilter.Overlay] : []),
      ...(gaussianSplats ? [SceneFilter.GaussianSplats] : []),
    ];
    // A single scan (e.g. RoviScan) could have not pano at all,
    // so derive its filters from the data it actually holds.
  } else if (isSingleScanSection(target)) {
    // If there's a pano, Pano filter is supported
    const pano = selectChildDepthFirst(target, isIElementImg360)({ iElements });
    // If the parent dataset has a viewable point cloud, PointCloud filter is supported
    const dataset = selectAncestor(
      target,
      isIElementGenericDataset,
    )({ iElements });
    // If the parent dataset has gaussian splats, GaussianSplats filter is supported
    const { pointClouds, gaussianSplats } = dataset
      ? selectDataSessionContainedData(dataset, {
          pointCloudFilter: (pc) =>
            selectIsPointCloudViewable(pc)({ iElements }),
          gaussianSplatsFilter: isIElementViewableGaussianSplats,
        })({ iElements })
      : { pointClouds: false, gaussianSplats: false };
    return [
      ...(pano ? [SceneFilter.Pano] : []),
      ...(pointClouds ? [SceneFilter.PointCloud, SceneFilter.Overlay] : []),
      ...(gaussianSplats ? [SceneFilter.GaussianSplats] : []),
    ];
  } else if (isIElementGaussianSplats(target)) {
    return [SceneFilter.GaussianSplats];
  } else if (isIElementCAD(target)) {
    return [SceneFilter.Cad, SceneFilter.Overlay];
  } else if (isIElementGenericPointCloudStream(target)) {
    return [SceneFilter.PointCloud, SceneFilter.Overlay];
  }
  return [];
}

const selectTargetElement = createSelector(
  [
    selectIElementsSlice,
    (state: OnlyIElementsStore, current: IElement) => current,
    (
      state: OnlyIElementsStore,
      current: IElement,
      shouldUseIntensityData: boolean,
    ) => shouldUseIntensityData,
  ],
  (iElements, current, shouldUseIntensityData) => {
    if (isIElementSectionWithTypeHint(current, IElementTypeHint.orbisScan)) {
      // Orbis mobile scan should pick the odometry path (if exists)
      return (
        selectChildDepthFirst(
          current,
          isIElementOdometryPathSection,
        )({ iElements }) ?? current
      );
    } else if (isIElementPhotogrammetryScan(current)) {
      // Photogrammetry scans should pick the orthomosaic (if exists)
      return (
        selectChildDepthFirst(current, isIElementOrthomosaic)({ iElements }) ??
        current
      );
    } else if (isSingleScanSection(current)) {
      // A generic single scan should pick the first 360 in its sub-tree
      return (
        selectDataSet360s(
          current,
          true,
          shouldUseIntensityData,
        )({ iElements }).at(0) ?? current
      );
    }

    // If nothing could be found, return the current element
    return current;
  },
);
